import { GameState } from '../game/GameState';
import { DataRegistry } from './DataRegistry';
import { ModifierManager } from './ModifierManager';

export class ProvinceManager {
  static readonly instance = new ProvinceManager();

  selectProvince(id: string) {
    GameState.instance.requireCampaign().selectedProvinceId = id;
  }

  constructBuilding(provinceId: string, buildingId: string) {
    const campaign = GameState.instance.requireCampaign();
    const registry = DataRegistry.instance;
    const province = campaign.provinces[provinceId];
    const building = registry.buildings[buildingId];
    if (!province || !building) return false;
    if (province.buildings.includes(buildingId)) return this.upgradeBuilding(provinceId, buildingId);
    if (province.constructionQueue.some((item) => item.buildingId === buildingId)) {
      GameState.instance.log(`${building.name} is already queued.`, 'warning');
      return false;
    }
    if (!this.meetsBuildingRequirements(provinceId, buildingId)) return false;
    const faction = campaign.factions[province.owner];
    if (!faction || faction.treasury < building.costGold) {
      GameState.instance.log('Not enough gold.', 'warning');
      return false;
    }
    faction.treasury -= building.costGold;
    province.constructionQueue.push({
      buildingId,
      remainingMonths: ModifierManager.instance.effectiveConstructionMonths(province.owner, building.buildMonths),
      targetLevel: 1,
    });
    GameState.instance.log(`Started construction: ${building.name}.`, 'success');
    return true;
  }

  upgradeBuilding(provinceId: string, buildingId: string) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[provinceId];
    const building = DataRegistry.instance.buildings[buildingId];
    if (!province || !building || !province.buildings.includes(buildingId)) return false;
    if (province.constructionQueue.some((item) => item.buildingId === buildingId)) {
      GameState.instance.log(`${building.name} is already being upgraded.`, 'warning');
      return false;
    }
    const level = province.buildingLevels[buildingId] ?? 1;
    const maxLevel = building.maxLevel ?? 1;
    if (level >= maxLevel) {
      GameState.instance.log(`${building.name} is already at maximum level.`, 'warning');
      return false;
    }
    if (!this.meetsBuildingRequirements(provinceId, buildingId)) return false;
    const cost = this.upgradeCost(buildingId, level + 1);
    const faction = campaign.factions[province.owner];
    if (!faction || faction.treasury < cost) {
      GameState.instance.log(`Upgrading ${building.name} requires ${cost} gold.`, 'warning');
      return false;
    }
    faction.treasury -= cost;
    province.constructionQueue.push({
      buildingId,
      remainingMonths: ModifierManager.instance.effectiveConstructionMonths(province.owner, Math.max(2, Math.round(building.buildMonths * 0.75 * (level + 1)))),
      targetLevel: level + 1,
    });
    GameState.instance.log(`Started ${building.name} level ${level + 1} upgrade.`, 'success');
    return true;
  }

  upgradeCost(buildingId: string, targetLevel: number) {
    const building = DataRegistry.instance.buildings[buildingId];
    if (!building) return 0;
    return Math.round(building.costGold * Math.pow(building.upgradeCostMultiplier ?? 1.6, Math.max(0, targetLevel - 1)));
  }

  cancelConstruction(provinceId: string, index: number) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[provinceId];
    const item = province?.constructionQueue[index];
    const building = item ? DataRegistry.instance.buildings[item.buildingId] : undefined;
    if (!province || !item || !building) return false;
    province.constructionQueue.splice(index, 1);
    const paid = item.targetLevel && item.targetLevel > 1 ? this.upgradeCost(item.buildingId, item.targetLevel) : building.costGold;
    campaign.factions[province.owner].treasury += Math.floor(paid * 0.5);
    GameState.instance.log(`Cancelled ${building.name}; half the cost was refunded.`, 'warning');
    return true;
  }

  suppressUnrest(provinceId: string) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[provinceId];
    if (!province) return false;
    const faction = campaign.factions[province.owner];
    const cost = 25;
    if (faction.treasury < cost) {
      GameState.instance.log('Suppressing unrest requires 25 gold.', 'warning');
      return false;
    }
    faction.treasury -= cost;
    province.unrest = Math.max(0, province.unrest - 8);
    province.loyalty = Math.min(100, province.loyalty + 3);
    province.prosperity = Math.max(0, province.prosperity - 2);
    GameState.instance.log(`Unrest suppressed in ${DataRegistry.instance.provinces[provinceId]?.name}.`, 'success');
    return true;
  }

  encourageAutonomy(provinceId: string) {
    const province = GameState.instance.requireCampaign().provinces[provinceId];
    if (!province) return false;
    province.autonomy = Math.min(90, province.autonomy + 10);
    province.unrest = Math.max(0, province.unrest - 5);
    province.loyalty = Math.min(100, province.loyalty + 7);
    GameState.instance.log(`Local privileges were expanded in ${DataRegistry.instance.provinces[provinceId]?.name}.`, 'success');
    return true;
  }

  centralizeAdministration(provinceId: string) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[provinceId];
    const faction = province ? campaign.factions[province.owner] : undefined;
    if (!province || !faction) return false;
    const cost = 35;
    if (faction.treasury < cost) {
      GameState.instance.log('Centralizing administration requires 35 gold.', 'warning');
      return false;
    }
    faction.treasury -= cost;
    province.autonomy = Math.max(0, province.autonomy - 8);
    province.unrest = Math.min(100, province.unrest + 3);
    province.loyalty = Math.max(0, province.loyalty - 2);
    GameState.instance.log(`Royal officials reduced autonomy in ${DataRegistry.instance.provinces[provinceId]?.name}.`, 'warning');
    return true;
  }

  auditOfficials(provinceId: string) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[provinceId];
    const faction = province ? campaign.factions[province.owner] : undefined;
    if (!province || !faction) return false;
    const cost = 30;
    if (faction.treasury < cost) {
      GameState.instance.log('An administrative audit requires 30 gold.', 'warning');
      return false;
    }
    faction.treasury -= cost;
    province.corruption = Math.max(0, province.corruption - 8);
    province.unrest = Math.min(100, province.unrest + 1);
    GameState.instance.log(`Officials were audited in ${DataRegistry.instance.provinces[provinceId]?.name}.`, 'success');
    return true;
  }

  setTaxRate(provinceId: string, rate: number) {
    const province = GameState.instance.requireCampaign().provinces[provinceId];
    if (!province) return false;
    province.taxRate = Math.max(0.5, Math.min(1.5, Math.round(rate * 100) / 100));
    GameState.instance.log(`Tax rate in ${DataRegistry.instance.provinces[provinceId]?.name} set to ${Math.round(province.taxRate * 100)}%.`);
    return true;
  }

  setSupplyDepot(provinceId: string) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[provinceId];
    if (!province) return false;
    const faction = campaign.factions[province.owner];
    if (province.supplyDepot) {
      GameState.instance.log('A supply depot already exists here.', 'warning');
      return false;
    }
    if (faction.treasury < 35) {
      GameState.instance.log('A supply depot requires 35 gold.', 'warning');
      return false;
    }
    faction.treasury -= 35;
    province.supplyDepot = true;
    province.storage.rations = (province.storage.rations ?? 0) + 25;
    GameState.instance.log(`Supply depot established in ${DataRegistry.instance.provinces[provinceId]?.name}.`, 'success');
    return true;
  }

  improveRoads(provinceId: string) {
    return this.upgradeProvinceLevel(provinceId, 'roadLevel', 30, 'Roads improved');
  }

  buildFortification(provinceId: string) {
    return this.upgradeProvinceLevel(provinceId, 'fortLevel', 60, 'Fortifications expanded');
  }

  expandPort(provinceId: string) {
    if (!DataRegistry.instance.provinces[provinceId]?.port) {
      GameState.instance.log('Only port provinces can expand harbor works.', 'warning');
      return false;
    }
    return this.upgradeProvinceLevel(provinceId, 'portLevel', 55, 'Port works expanded');
  }

  processConstruction() {
    const campaign = GameState.instance.requireCampaign();
    const registry = DataRegistry.instance;
    for (const province of Object.values(campaign.provinces)) {
      for (const item of province.constructionQueue) item.remainingMonths--;
      const done = province.constructionQueue.filter((item) => item.remainingMonths <= 0);
      province.constructionQueue = province.constructionQueue.filter((item) => item.remainingMonths > 0);
      for (const item of done) {
        if (!province.buildings.includes(item.buildingId)) province.buildings.push(item.buildingId);
        province.buildingLevels[item.buildingId] = Math.max(province.buildingLevels[item.buildingId] ?? 0, item.targetLevel ?? 1);
        const building = registry.buildings[item.buildingId];
        if (building?.category === 'fortification') province.fortLevel = Math.max(province.fortLevel ?? 0, province.buildingLevels[item.buildingId]);
        if (building?.requiresPort) province.portLevel = Math.max(province.portLevel ?? 0, 1);
        GameState.instance.log(`${building?.name ?? item.buildingId} level ${province.buildingLevels[item.buildingId]} completed in ${registry.provinces[province.id]?.name}.`, 'success');
      }
    }
  }

  meetsBuildingRequirements(provinceId: string, buildingId: string) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[provinceId];
    const provinceData = DataRegistry.instance.provinces[provinceId];
    const building = DataRegistry.instance.buildings[buildingId];
    if (!province || !provinceData || !building) return false;
    if (building.requiresPort && !provinceData.port) {
      GameState.instance.log(`${building.name} requires a port province.`, 'warning');
      return false;
    }
    const missingBuilding = (building.requiresBuildings ?? []).find((requirement) => !province.buildings.includes(requirement));
    if (missingBuilding) {
      GameState.instance.log(`${building.name} requires ${DataRegistry.instance.buildings[missingBuilding]?.name ?? missingBuilding}.`, 'warning');
      return false;
    }
    if (building.requiredDeposits?.length && !building.requiredDeposits.some((deposit) => provinceData.deposits.includes(deposit))) {
      GameState.instance.log(`${building.name} requires a suitable local deposit.`, 'warning');
      return false;
    }
    return true;
  }

  private upgradeProvinceLevel(provinceId: string, key: 'roadLevel' | 'fortLevel' | 'portLevel', cost: number, label: string) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[provinceId];
    if (!province) return false;
    const faction = campaign.factions[province.owner];
    const nextLevel = (province[key] ?? 0) + 1;
    const scaledCost = Math.round(cost * (1 + Math.max(0, nextLevel - 1) * 0.6));
    if (faction.treasury < scaledCost) {
      GameState.instance.log(`${label} requires ${scaledCost} gold.`, 'warning');
      return false;
    }
    faction.treasury -= scaledCost;
    province[key] = nextLevel;
    GameState.instance.log(`${label} to level ${nextLevel} in ${DataRegistry.instance.provinces[provinceId]?.name}.`, 'success');
    return true;
  }
}
